import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useLazyGetPosApprovalRequestQuery, useStartOtpCycleMutation, useVerifyOtpCycleMutation, useResendOtpCycleMutation,
} from '../services/discountApi';
import { useToast } from '../components/common/ToastProvider';

// Polls one discount_approval_request (voucher sale discount / quantity /
// cancellation) until a manager approves or rejects it, with an optional
// manager-OTP path. State is local — unlike the order-discount flow
// (useDiscountOtpCycle / discountSlice) nothing here must survive unmount.
// State is keyed by requestId, so a new request always starts clean.
export function useVoucherApproval(requestId, { onApproved, onRejected } = {}) {
  const showToast = useToast();
  const [state, setState] = useState({ id: null, status: null, approvedBy: '', cycle: null });
  const current = state.id === requestId
    ? state
    : { id: requestId, status: requestId ? 'pending' : null, approvedBy: '', cycle: null };
  const update = useCallback((changes) => {
    setState((prev) => ({ ...(prev.id === requestId ? prev : { id: requestId, status: 'pending', approvedBy: '', cycle: null }), ...changes }));
  }, [requestId]);

  const [getRequest] = useLazyGetPosApprovalRequestQuery();
  const [startOtp, { isLoading: isStarting }] = useStartOtpCycleMutation();
  const [resendOtp, { isLoading: isResending }] = useResendOtpCycleMutation();
  const [verifyOtp, { isLoading: isVerifying }] = useVerifyOtpCycleMutation();
  const cbs = useRef({ onApproved, onRejected });
  useEffect(() => { cbs.current = { onApproved, onRejected }; });

  const status = current.status;
  useEffect(() => {
    if (!requestId || status !== 'pending') return undefined;
    let stopped = false;
    const poll = async () => {
      try {
        const res = await getRequest(requestId, false).unwrap();
        const req = res?.data?.discount_approval_request;
        if (!req || stopped) return;
        if (req.status === 'approved') {
          stopped = true;
          update({ status: 'approved', approvedBy: req.approved_by_name || '' });
          cbs.current.onApproved?.(req);
        } else if (['rejected', 'cancelled', 'expired'].includes(req.status)) {
          stopped = true;
          update({ status: req.status });
          cbs.current.onRejected?.(req);
        }
      } catch {
        // keep polling
      }
    };
    poll();
    const iv = setInterval(poll, 4000);
    return () => { stopped = true; clearInterval(iv); };
  }, [requestId, status, getRequest, update]);

  const startOtpCycle = useCallback(async () => {
    try {
      const res = await startOtp(requestId).unwrap();
      const c = res?.data?.otp_cycle;
      const n = (c.recipients || []).length;
      update({ cycle: { id: c.id, recipients: c.recipients || [] } });
      showToast(`6-digit code texted to ${n || 'the'} approver${n === 1 ? '' : 's'}`, 'info');
    } catch (err) {
      showToast(err?.data?.error || 'Could not send the OTP', 'error');
    }
  }, [requestId, startOtp, showToast, update]);

  const resendOtpCycle = useCallback(async () => {
    if (!current.cycle) return;
    try {
      await resendOtp({ requestId, cycleId: current.cycle.id }).unwrap();
      showToast('New code sent', 'info');
    } catch (err) {
      showToast(err?.data?.error || 'Could not resend the code', 'error');
    }
  }, [requestId, current.cycle, resendOtp, showToast]);

  const verifyOtpCode = useCallback(async (code) => {
    if (!current.cycle) return false;
    try {
      const res = await verifyOtp({ requestId, cycleId: current.cycle.id, code }).unwrap();
      const req = res?.data?.discount_approval_request;
      update({ status: 'approved', approvedBy: req?.approved_by_name || '' });
      cbs.current.onApproved?.(req);
      return true;
    } catch (err) {
      showToast(err?.data?.error || 'Incorrect verification code', 'error');
      return false;
    }
  }, [requestId, current.cycle, verifyOtp, showToast, update]);

  return {
    status: current.status, approvedBy: current.approvedBy, cycle: current.cycle,
    isStarting, isResending, isVerifying, startOtpCycle, resendOtpCycle, verifyOtpCode,
  };
}
